import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, OnDestroy, inject } from "@angular/core";
import { Token } from "@app/models/token.interface";
import { AuthenticationService } from "@app/services/authentication.service";
import { LoggingService } from "@app/services/logging.service";
import { BruudToastrService } from "@app/services/message.service";
import { AppSettings } from "app/app.settings";
import { Subject, throwError } from "rxjs";
import { takeUntil, catchError, switchMap, map, mergeMap } from "rxjs/operators";
import { OriginOidcService } from "./origin-oidc.service";
import { ProfileService } from "@app/services/profile.service";

@Injectable({
   providedIn: "root"
})
export class OriginSsoService implements OnDestroy {
   private originOidcService = inject(OriginOidcService);
   private authService = inject(AuthenticationService);
   private log = inject(LoggingService);
   private messageService = inject(BruudToastrService);
   private httpClient = inject(HttpClient);
   private appSettings = inject(AppSettings);
   private profileService = inject(ProfileService);

   private destroyed: Subject<void> = new Subject<void>();

   private readonly targetUri: string = "/ng/origin/page";

   public ngOnDestroy(): void {
      this.destroyed.next();
      this.destroyed.complete();
   }

   public get isAuthenticated(): boolean {
      return this.authService.isOriginSsoAuthenticated;
   }

   public onOriginLoginCompleted(): void {
      this.login();
   }

   private login(): void {
      this.log.info("Logging in with origin SSO");

      this.originOidcService.getAccessToken().then(ssoToken => {
         const headers = new HttpHeaders({
            Authorization: `bearer ${ssoToken}`,
            "Content-Type": "application/json"
         });

         this.httpClient
            .get<Token>(`${this.appSettings.baseCoreApiUrl}/originsso/token`, {
               headers: headers
            })
            .pipe(
               takeUntil(this.destroyed),
               catchError(err => {
                  if (err instanceof HttpErrorResponse) {
                     if (err.status == 401) {
                        this.messageService.showTranslatedError(
                           "MC_ORIGINSSO_ALREADYLOGGEDIN",
                           "Error"
                        );
                     }
                     if (err.status == 404) {
                        this.messageService.showTranslatedError(
                           "MC_ORIGINSSO_USERNOTFOUND",
                           "Error"
                        );
                     }
                     return throwError(err);
                  }
               }),
               switchMap(token => this.authService.processToken(token)),
               map(profile => this.authService.processProfile(profile))
            )
            .subscribe(_ => {
               this.log.info("origin SSO login complete");
               location.href = `${this.targetUri}/${localStorage.getItem(
                  "pageType"
               )}`;
            });
      });
   }

   public logout(): void {
      this.profileService
         .revokeToken()
         .pipe(
            takeUntil(this.destroyed),
            mergeMap(_ => this.originOidcService.isLoggedInObservable()),
            switchMap(loggedIn => {
               if (loggedIn) {
                  return this.originOidcService.logoutObservable();
               }

               this.authService.logout();
            })
         )
         .subscribe();
   }
}
